export const PROGRESS_FAST_DURATION = 2000
export const PROGRESS_FAST_TARGET = 70
export const PROGRESS_FINISH_DURATION = 100
export const MAX_PROGRESS = 100

/**
 * Cubic ease-out function: starts fast, slows down at the end
 */
export function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3)
}

export type ProgressAnimationConfig = {
    /** Duration of the slow phase in milliseconds */
    slowDuration: number
    /** Target progress value (default: 100) */
    maxProgress?: number
    /** Callback to update progress value */
    onProgress: (value: number) => void
    /** Callback to check if animation should stop */
    shouldStop?: () => boolean
}

export type ProgressAnimationController = {
    /** Start the loading animation */
    start: () => void
    /** Run fast finish animation to 100% */
    finish: (onComplete?: () => void) => void
    /** Cancel any running animation */
    cancel: () => void
    /** Get current progress value */
    getProgress: () => number
}

/**
 * Creates a two-phase progress animation controller:
 * - Phase 1 (FAST): Quick ease-out to 70% over 2 seconds
 * - Phase 2 (SLOW): Linear crawl from 70% to maxProgress over slowDuration
 */
export function createProgressAnimation(config: ProgressAnimationConfig): ProgressAnimationController {
    const { slowDuration, onProgress, shouldStop } = config

    let animationFrameId: number | null = null
    let currentProgress = 0

    function cancelAnimation(): void {
        if (animationFrameId !== null) {
            cancelAnimationFrame(animationFrameId)
            animationFrameId = null
        }
    }

    function setProgress(value: number): void {
        currentProgress = value
        onProgress(value)
    }

    function start(): void {
        const startTime = Date.now()

        const animate = () => {
            if (shouldStop?.()) {
                return
            }

            const elapsed = Date.now() - startTime

            if (elapsed < PROGRESS_FAST_DURATION) {
                // Phase 1: FAST - ease-out to 70%
                const t = elapsed / PROGRESS_FAST_DURATION
                setProgress(easeOutCubic(t) * PROGRESS_FAST_TARGET)
            } else {
                // Phase 2: SLOW - linear crawl to maxProgress
                const elapsedInSlow = elapsed - PROGRESS_FAST_DURATION
                const t = Math.min(elapsedInSlow / slowDuration, 1)
                setProgress(PROGRESS_FAST_TARGET + t * (MAX_PROGRESS - PROGRESS_FAST_TARGET))
            }

            if (currentProgress < MAX_PROGRESS) {
                animationFrameId = requestAnimationFrame(animate)
            }
        }

        animationFrameId = requestAnimationFrame(animate)
    }

    function finish(onComplete?: () => void): void {
        cancelAnimation()

        const startValue = currentProgress
        const startTime = Date.now()

        const animateFinish = () => {
            const elapsed = Date.now() - startTime
            const t = Math.min(elapsed / PROGRESS_FINISH_DURATION, 1)

            setProgress(startValue + (PROGRESS_FINISH_DURATION - startValue) * easeOutCubic(t))

            if (t < 1) {
                animationFrameId = requestAnimationFrame(animateFinish)
            } else {
                setProgress(PROGRESS_FINISH_DURATION)
                onComplete?.()
            }
        }

        animationFrameId = requestAnimationFrame(animateFinish)
    }

    return {
        start,
        finish,
        cancel: cancelAnimation,
        getProgress: () => currentProgress,
    }
}
